import React, { CSSProperties, useState, useEffect } from 'react'

import { Button } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Range, getTrackBackground, useThumbOverlap } from 'react-range'
import { IThumbProps, ITrackProps } from 'react-range/lib/types'

import MinusIcon from 'public/svg/minus.svg'
import PlusIcon from 'public/svg/plus.svg'

import {
  StyledTrackContainer,
  StyledDot,
  StyledSlider,
  StyledThumb,
  StyledTrack,
  StyledLabel,
} from './index.styled'

export type Props = {
  step: number
  // min and max comes from api and don't change during the process
  min: number
  max: number
  value: Array<number>
  onChange: (v: Array<number>) => void
  className?: string
  heading?: string
  isMobile?: boolean
}

function ThumbLabel({
  rangeRef,
  values,
  index,
}: {
  rangeRef: Range | null
  values: number[]
  index: number
  isMobile?: boolean
}) {
  const [labelValue, labelStyle] = useThumbOverlap(
    rangeRef,
    values,
    index,
    1,
    ' - ',
    (value) => value,
  )

  return (
    <>
      <StyledLabel data-label={index} style={labelStyle as CSSProperties}>
        {labelValue as any}
      </StyledLabel>
      <StyledDot data-label={index}></StyledDot>
    </>
  )
}

const SingleSlider = ({
  step,
  min,
  max,
  value,
  onChange,
  className,
  heading,
  isMobile = false,
}: Props) => {
  const COLORS = ['#2D87F3', '#DBDFE5']

  const rangeRef = React.useRef<Range | null>(null)
  const [localValue, setLocalValue] = useState(value)

  const Thumb = ({ props, index }: { props: IThumbProps; index: number }) => {
    return (
      <StyledThumb {...props}>
        <ThumbLabel
          rangeRef={rangeRef.current}
          values={localValue}
          index={index}
          isMobile={isMobile}
        />
      </StyledThumb>
    )
  }

  const setValue = (newValues: number[]) => {
    setLocalValue(
      newValues.map((v) => {
        if (v < min) return min
        if (v > max) return max
        return v
      }),
    )
  }

  useEffect(() => {
    setValue(value)
  }, [value[0], value[1]])

  const Track = ({
    props,
    children,
  }: {
    props: ITrackProps
    children: React.ReactNode
  }) => {
    const trackStyles = {
      background: getTrackBackground({
        values: localValue,
        colors: COLORS,
        min,
        max,
      }),
    }

    return (
      <>
        {heading && <Text size="s4">{heading}</Text>}
        <StyledTrackContainer style={{ ...props.style }}>
          <Button
            color="ghost"
            size="sm"
            onClick={() => setValue([localValue[0] - 1])}
            className="slider-track-button"
          >
            <MinusIcon
              className="slider-track-icon minus"
              height={isMobile ? '3' : '4'}
              viewBox="0 0 12 3"
              style={{
                marginBottom: 3,
              }}
            />
          </Button>
          <StyledTrack
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            ref={props.ref}
            style={trackStyles}
          >
            {children}
          </StyledTrack>
          <Button
            color="ghost"
            size="sm"
            onClick={() => setValue([localValue[0] + 1])}
            className="slider-track-button"
          >
            <PlusIcon
              className="slider-track-icon plus"
              height={isMobile ? '16' : '20'}
              viewBox="0 0 16 16"
            />
          </Button>
        </StyledTrackContainer>
      </>
    )
  }

  return (
    <StyledSlider className={className}>
      <Range
        ref={rangeRef}
        values={localValue}
        onChange={setValue}
        onFinalChange={onChange}
        renderThumb={Thumb}
        renderTrack={Track}
        step={step}
        min={min}
        max={max}
      />
    </StyledSlider>
  )
}

export { SingleSlider }
